import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/** Username columns editable from Artist Mapping (UIDs are never edited here). */
export const ARTIST_MAPPING_PLATFORMS = [
  { key: 'tiktok', column: 'TikTok', uidColumn: 'TikTok UID', label: 'TikTok', placeholder: 'username' },
  { key: 'instagram', column: 'Instagram', uidColumn: 'Instagram UID', label: 'Instagram', placeholder: 'username' },
  { key: 'youtube', column: 'YouTube', label: 'YouTube', placeholder: 'channel ID/username' },
  { key: 'soundcloud', column: 'SoundCloud', label: 'SoundCloud', placeholder: 'username' },
  { key: 'spotify', column: 'Spotify', label: 'Spotify', placeholder: 'username' },
  { key: 'apple', column: 'Apple Music', label: 'Apple Music', placeholder: 'username' },
  { key: 'genius', column: 'Genius', label: 'Genius', placeholder: 'username' },
  { key: 'twitter', column: 'Twitter', label: 'Twitter', placeholder: 'username' },
  { key: 'shazam', column: 'Shazam', label: 'Shazam', placeholder: 'username' },
]

export const MAPPING_EDIT_COLUMN = '__mapping_edit__'

function displayValue(row, column) {
  const raw = row?.[column]
  if (raw == null) return ''
  const s = String(raw).trim()
  if (!s || s.toLowerCase() === 'nan' || s.toLowerCase() === 'none') return ''
  return s
}

function positionMenu(anchorEl, { width = 280 } = {}) {
  const rect = anchorEl.getBoundingClientRect()
  const w = Math.min(width, window.innerWidth - 24)
  let left = rect.left
  if (left + w > window.innerWidth - 12) {
    left = Math.max(12, window.innerWidth - w - 12)
  }
  let top = rect.bottom + 6
  const estimatedHeight = 320
  if (top + estimatedHeight > window.innerHeight - 12) {
    top = Math.max(12, rect.top - estimatedHeight - 6)
  }
  return { left, top, width: w }
}

/**
 * Pencil control → platform list → username editor.
 * Clears paired UID client-side when IG/TT handle is cleared or changed.
 */
export default function MappingPlatformEdit({
  row,
  saving = false,
  error = null,
  onSave,
}) {
  const [open, setOpen] = useState(false)
  const [pinned, setPinned] = useState(false)
  const [menuPos, setMenuPos] = useState(null)
  const [editing, setEditing] = useState(null)
  const [draft, setDraft] = useState('')
  const anchorRef = useRef(null)
  const hideTimer = useRef(null)
  const inputRef = useRef(null)

  function clearHide() {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current)
      hideTimer.current = null
    }
  }

  function scheduleHide() {
    if (pinned) return
    clearHide()
    hideTimer.current = setTimeout(() => {
      setOpen(false)
      setEditing(null)
      setMenuPos(null)
    }, 220)
  }

  function showMenu() {
    clearHide()
    if (!anchorRef.current) return
    setMenuPos(positionMenu(anchorRef.current))
    setOpen(true)
  }

  function togglePin(event) {
    event.preventDefault()
    event.stopPropagation()
    if (open && pinned) {
      closeMenu()
      return
    }
    setPinned(true)
    showMenu()
  }

  function startEdit(platform) {
    setEditing(platform)
    setDraft(displayValue(row, platform.column))
    setPinned(true)
  }

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editing])

  function closeMenu() {
    clearHide()
    setPinned(false)
    setOpen(false)
    setEditing(null)
    setMenuPos(null)
    setDraft('')
  }

  useEffect(() => {
    if (!open) return undefined
    function onKey(event) {
      if (event.key === 'Escape') {
        closeMenu()
      }
    }
    function onScroll() {
      if (anchorRef.current && open) {
        setMenuPos(positionMenu(anchorRef.current))
      }
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  async function submit(nextValue) {
    if (!editing || !onSave) return
    const payload = {
      platform: editing.key,
      column: editing.column,
      uidColumn: editing.uidColumn || null,
      username: nextValue,
      previousUsername: displayValue(row, editing.column),
    }
    // Hide immediately while the request is in-flight (pencil keeps saving pulse).
    closeMenu()
    try {
      await onSave(payload)
    } catch {
      // DataTable surfaces the error under the pencil / banner.
    }
  }

  const mappingId = row?.id
  const missingId = mappingId == null || String(mappingId).trim() === ''

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        title={
          missingId
            ? 'Missing mapping id — reload the table'
            : 'Edit platform usernames'
        }
        disabled={missingId}
        onClick={togglePin}
        onMouseEnter={showMenu}
        onMouseLeave={scheduleHide}
        className={[
          'inline-flex h-7 w-7 items-center justify-center rounded-md border border-transparent text-gray-400 transition',
          'hover:border-gray-200 hover:bg-white hover:text-gray-700',
          open ? 'border-gray-200 bg-white text-gray-800 shadow-sm' : '',
          saving ? 'animate-pulse' : '',
        ].join(' ')}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {error ? (
        <span className="mt-1 block max-w-[7rem] truncate text-[10px] text-rose-600" title={error}>
          {error}
        </span>
      ) : null}
      {open && menuPos
        ? createPortal(
            <div
              role="menu"
              className="fixed z-[9999] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl"
              style={{ left: menuPos.left, top: menuPos.top, width: menuPos.width }}
              onMouseEnter={clearHide}
              onMouseLeave={scheduleHide}
            >
              {editing ? (
                <div className="p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <button
                      type="button"
                      className="text-[11px] font-medium text-gray-500 hover:text-gray-800"
                      onClick={() => setEditing(null)}
                    >
                      ← Platforms
                    </button>
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-700">
                      {editing.label}
                    </span>
                  </div>
                  <input
                    ref={inputRef}
                    type="text"
                    value={draft}
                    disabled={saving}
                    placeholder={editing.placeholder || 'username'}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        submit(draft.trim())
                      }
                    }}
                    className="mb-2 w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm text-gray-900 shadow-sm focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-400"
                  />
                  {editing.uidColumn ? (
                    <p className="mb-2 text-[10px] leading-snug text-gray-500">
                      Username only. Clearing or changing this handle also clears{' '}
                      {editing.uidColumn}.
                    </p>
                  ) : null}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => submit(draft.trim())}
                      className="rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                    >
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      disabled={saving || !displayValue(row, editing.column)}
                      onClick={() => submit('')}
                      className="rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-40"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              ) : (
                <div className="max-h-80 overflow-y-auto py-1">
                  <div className="border-b border-gray-100 px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    Edit username
                  </div>
                  {ARTIST_MAPPING_PLATFORMS.map((platform) => {
                    const value = displayValue(row, platform.column)
                    return (
                      <button
                        key={platform.key}
                        type="button"
                        role="menuitem"
                        onClick={() => startEdit(platform)}
                        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-gray-50"
                      >
                        <span className="font-medium text-gray-800">{platform.label}</span>
                        <span
                          className={[
                            'max-w-[9rem] truncate text-xs',
                            value ? 'text-gray-600' : 'text-gray-300',
                          ].join(' ')}
                          title={value || undefined}
                        >
                          {value || '—'}
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
